import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ReferenceArrayInput,
  SaveButton,
  useDataProvider,
  useNotify
} from 'react-admin';
import { Grid } from '@material-ui/core/';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { FormSpy } from 'react-final-form';
import { connect } from 'react-redux';

import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import GridInput from '../../../core/components/inputs/GridInput';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import GridInputChoices from './GridInputChoices';
import {
  getFundFamiliesIds,
  referenceArrayInputProps,
  validateFunds,
  validateInvestors
} from './utils';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const AddAuthorizationDialog = props => {
  //NOSONAR javascript:S3776
  // when perf debugging, check what's changed in the properties
  const {
    setOpenDialog,
    userTypeRoleCheck,
    handleAddAuthorization,
    usedIds,
    reduxFunds
  } = props;

  const [submitting, setSubmitting] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const [formValues, setFormValues] = useState();

  const allFunds = useMemo(() => Object.values(reduxFunds), [reduxFunds]);

  const formRef = useRef();

  const isDisabledAddButton = useMemo(() => {
    const isValidSelectionForInvestors =
      formValues && validateInvestors(formValues.investors) === undefined; //NOSONAR cwe
    const isValidSelectionForFunds =
      formValues && validateFunds(formValues.funds) === undefined;

    return !isValidSelectionForFunds && !isValidSelectionForInvestors;
  }, [formValues]);

  const group = userTypeRoleCheck ? 'Investor' : 'Fund';

  const handleClose = useCallback(() => {
    setOpenDialog(false);
  }, [setOpenDialog]);

  const handleSubmit = useCallback(() => {
    const values = formValues;
    if (Array.isArray(values.investors) && values.investors.length > 0) {
      setSubmitting(true);
      dataProvider
        .getList('fundinvestors', {
          pagination: { perPage: -1 },
          filter: { investorId: `[${values.investors}]` }
        })
        .then(({ data }) => {
          handleAddAuthorization(data);
          setSubmitting(false);
          setOpenDialog(false);
          notify('ra.notification.added', {
            messageArgs: { smart_count: data.length }
          });
        })
        .catch(error => {
          setSubmitting(false);
          notifyServerError(error, undefined);
        });
    } else if (Array.isArray(values.funds) && values.funds.length > 0) {
      setSubmitting(true);
      const selectedFunds =
        allFunds.length === values.funds.length
          ? allFunds
          : allFunds.filter(item => values.funds.includes(item.id));

      const fundFamiliesIds = [];
      getFundFamiliesIds(selectedFunds, fundFamiliesIds);

      if (fundFamiliesIds.length > 0) {
        dataProvider
          .getMany('fundfamilies', {
            ids: fundFamiliesIds,
            target: 'id'
          })
          .then(({ data }) => {
            const dataWithFundFamilies = selectedFunds.map(fund => {
              const fundFamily = data.find(
                item => item.id === fund.fundFamilyId
              );
              return {
                fundId: fund.id,
                fundName: fund.name,
                fundFamilyId: fundFamily && fundFamily.id,
                fundFamilyName: fundFamily && fundFamily.name,
                investorId: '*'
              };
            });

            setOpenDialog(false);
            handleAddAuthorization(dataWithFundFamilies);
            notify('ra.notification.added', {
              messageArgs: {
                smart_count: dataWithFundFamilies.length
              }
            });
          })
          .catch(error => {
            setSubmitting(false);
            notifyServerError(error, undefined);
          });
      } else {
        setSubmitting(true);
        const dataWithFunds = selectedFunds.map(fund => {
          return {
            fundId: fund.id,
            fundName: fund.name,
            investorId: '*'
          };
        });
        setSubmitting(false);
        handleAddAuthorization(dataWithFunds);
        setOpenDialog(false);
        notify('ra.notification.added', {
          messageArgs: { smart_count: dataWithFunds.length }
        });
      }
    }
  }, [
    dataProvider,
    allFunds,
    handleAddAuthorization,
    notify,
    setOpenDialog,
    formValues,
    notifyServerError
  ]);

  const isRegular = userTypeRoleCheck;

  const additionalFilterProps = {
    attributeToFilter: 'fundFamilyName', // Should be named after the attribute we want to search for
    emptyText: 'Fund Families',
    placeholder: 'Fund Families'
  };

  return (
    <>
      <Dialog
        fullWidth
        open={true}
        aria-label={`Add ${group}`}
        maxWidth="md"
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return null;
          handleClose();
        }}
      >
        <DialogTitleWithCloseButton onClose={handleClose}>
          {`Add ${group}`}
        </DialogTitleWithCloseButton>
        <DialogContent>
          <CustomSimpleForm
            initialValues={{ investors: [], funds: [] }}
            toolbar={null}
            variant="standard"
            margin="normal"
          >
            <FormSpy>
              {newFormProps => {
                formRef.current = newFormProps.form;
                if (!isEqual(formValues, newFormProps.values)) {
                  setTimeout(() => setFormValues(newFormProps.values), 1);
                }
                return (
                  <Grid container spacing={1}>
                    <Grid item sm={12}>
                      <ReferenceArrayInput
                        {...referenceArrayInputProps(isRegular, false)}
                      >
                        <GridInputChoices
                          usedIds={isRegular ? undefined : usedIds.funds}
                          emptyText={
                            isRegular
                              ? 'There are no Investors to be displayed.'
                              : 'There are no Funds to be displayed.'
                          }
                        >
                          <GridInput
                            height="500px"
                            showAdditionalFilter={!isRegular}
                            additionalFilterProps={additionalFilterProps}
                          />
                        </GridInputChoices>
                      </ReferenceArrayInput>
                    </Grid>
                  </Grid>
                );
              }}
            </FormSpy>
          </CustomSimpleForm>
        </DialogContent>
        <DialogActions>
          <SaveButton
            saving={submitting}
            disabled={isDisabledAddButton}
            handleSubmitWithRedirect={handleSubmit}
            label="Add"
            icon={<></>}
          />
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
            disabled={submitting}
            disableFocusRipple
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const mapStateToProps = state => {
  return {
    reduxFunds: get(state, 'admin.resources.funds.data', {})
  };
};

export default connect(mapStateToProps, {})(AddAuthorizationDialog);
